/** @jsx jsx */
import { h, Fragment, Component} from "preact";
import { observer } from "mobx-react";
import { useMemo, useRef, useEffect } from 'preact/hooks';
import { jsx, css } from '@emotion/react';

import { filters } from '@searchspring/snap-toolbox';
import { DesktopFilterSummary } from "../FilterSummary/DesktopFilterSummary";

import classnames from 'classnames';

import { ArrowDown } from "../Icons/ArrowDown";
import { ArrowUp } from "../Icons/ArrowUp";



import {
  ThemeProvider,
  defaultTheme,
  FilterSummary,
  FacetSlider,
  FacetGridOptions,
  FacetPaletteOptions,
  FacetListOptions,
  FacetHierarchyOptions,
  StoreProvider,
  withStore,
  withController,
  withTheme,
  ControllerProvider,
} from '@searchspring/snap-preact-components';

import './sidebar.css'

@observer
export class Sidebar extends Component {
  render() {
    const store = this.props.controller.store;

    return (
      <ThemeProvider theme={defaultTheme}>
        <ControllerProvider controller={this.props.controller}>
          <StoreProvider store={store}>
            <SidebarContents />
          </StoreProvider>
        </ControllerProvider>
      </ThemeProvider>
    );
  }
}

@withController
@withStore
@observer
export class SidebarContents extends Component {
  render() {
    const {
      filters,
      facets,
    } = this.props.store;

    const leftColumnLayout = this.props.leftColumnLayout;
    
    return (
      <div class={ leftColumnLayout ? "ss-sidebar-container-left-column" : "ss-sidebar-container"}>
        <DesktopFilterSummary />
        {facets.length !== 0 && (
          <div className="ss__facets">
            {facets.map((facet, index) => (
              <>
               <Facet facet={facet} optionsSlot={<OptionsSlot />} disableOverflow index={index}/>
              </>  
            ))}
          </div>
        )}
      </div> 
		);
  }
}

export const Facet = withTheme(
	withController(
		observer(({ theme, controller, facet, index }) => {
			const store = controller.store;
			const { custom, facets } = store;
			if (facet.display == 'none') {
        return null;
      }

      if (window.location.href.includes('/learning-center') && facet.field !== "type") {
        return null;
      }

			// set facet overflow limit
			if (facet.overflow) {
				if (facet.display == 'palette') {
					facet.overflow.setLimit(30);
				} else {
					facet.overflow.setLimit(10);
				}
			}

			// should be set after overflow limits
			const values = facet.refinedValues;

      const facetListValues = [...values];

      if (facet.field == "type") {
        facetListValues.map( i => i.label = i.label.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '))
      }
      
			// check if facet should show scroll
			const scroll = facet.overflow && facet.overflow.enabled && !facet.overflow.remaining ? true : false;

      // scroll to top of facet list on click
      useEffect(() => {
        document.querySelectorAll('.ss__facet__options a').forEach( opt => opt.addEventListener('click', () => { document.querySelector('.ss__results')?.scrollIntoView({ top: 0, behavior: 'smooth' })}));
      }, [facetListValues]);

			return (
        facet && (
					<div
						id={`ss__facet--${facet.field}`}
						className={classnames(`ss__facet ss__facet--${facet.display}`, {
							'ss__facet--collapsed': facet.collapsed,
							'ss__facet--expanded': !facet.collapsed
						})}
					>
            <div className={classnames(`ss__dropdown ss__facet__dropdown ${facet.collapsed ? '' : 'ss__dropdown--open'}`)}>
                <div class="ss__dropdown__button" role="button"
                    aria-expanded={facet.collapsed ? 'false' : 'true'}
                    onClick={() => {
                        facet.toggleCollapse();
                    }}>
                    <div class="ss__facet__header" role="heading" aria-level="3" aria-label={`currently ${facet.collapsed ? 'collapsed' : 'open'} ${facet.label} facet dropdown`} ssa11y="true" tabindex="0">
                        {facet.label}
                    </div>
                    
                    {facet.collapsed ? <ArrowDown/> : <ArrowUp/> }
                  
                </div>

                <div class="ss__dropdown__content">
                    <div className="ss__facet__options">
                        {{
                            grid: <FacetGridOptions values={values} scroll={scroll} type={'facet'} />,
                            palette: <CustomFacetPaletteOptions facet={facet} scroll={scroll} type={'facet'} showLabel={true} showMore={true} />,
                            hierarchy: <FacetHierarchyOptions values={values} />,
                            slider: <FacetSlider facet={facet} />,
                        }[facet.display] || <FacetListOptions values={facetListValues} scroll={scroll} />}
                    </div>

                    {facet.overflow && facet.overflow.enabled && (
                        <div class="ss__facet__show-more-less" open={`${facet.overflow.remaining ? 'false' : 'true'}`} ssa11y="true" tabindex="0"
                        onClick={() => {
                            facet.overflow.toggle();
                        }}>
                          {facet.overflow.remaining ? document.querySelectorAll('.ss__facet-list-options__option').forEach( i => i.addEventListener('click', function() { document.querySelector('.ss__results')?.scrollIntoView({ top: 0, behavior: 'smooth' })})) :  document.querySelectorAll('.ss__facet-list-options__option').forEach( i => i.addEventListener('click', function() { document.querySelector('.ss__results')?.scrollIntoView({ top: 0, behavior: 'smooth' })}))}
                            <span>{facet.overflow.remaining ? window.Resources.searchspring.toolbar.show_more : window.Resources.searchspring.toolbar.show_less}</span>
                        </div>
                    )}
                </div>
            </div>
					</div>
				)
				)
		})
	)
);

const OptionsSlot = ({ facet, limit }) => {
  const { label } = facet;
  const labelClass  = facet.label;

  const optionHelper = useMemo(() => {
    const optionHelperJSONString = document.getElementById('optionHelpers')?.textContent;
    const optionHelpers = optionHelperJSONString ? JSON.parse(optionHelperJSONString) : {};
    return optionHelpers[label.toLowerCase()]?.valueHelper;
  }, [label]);

  return (
    <>
      {facetDisplay && <div className={`ss__facet-options-display ${labelClass}`}>{facetDisplay}</div>}
      {optionHelper && <OptionHelper html={optionHelper} />}
    </>
  );
};

const CustomFacetPaletteOptions = ({ facet }) => {
  const { values } = facet;
  const colorSwatches = JSON.parse(window.Resources.color_filters.textContent)

  const transformedValues = useMemo(() => {
    return values.filter(v => v.label in colorSwatches);
  }, [values]);

    const facetCss = useMemo(() => {
    const styles = {};

    transformedValues.forEach(v => {
      let colorFamily = v.label;
      let colorImage = colorSwatches?.[colorFamily].swatchImage;
      let colorFile = colorImage ? colorSwatches?.[colorFamily].swatchFile : null;
      let colorHex = colorSwatches?.[colorFamily].hexValue;
      let background = colorImage?.length ? `url("${colorFile}")` : colorHex;

      styles[`& .ss__facet-palette-options__option__palette--${filters.handleize(colorFamily)}`] = {
        display: 'block',
        background: background
      };
    });

    return [css(styles)];
  }, [transformedValues]);

  return <FacetPaletteOptions css={facetCss} values={transformedValues} columns={5} hideIcon gapSize='0px' />;
};
